/* =========================================================
   poc-demo-core.js full replacement v6
   Separate category navigation from tag/package filters
   Added:
   - Safe local POC binding to prevent "POC is not defined"
   - Safer render guard
   - Category URL parameter is reserved for catalog navigation
   - Tag/package filters no longer consume or delete category
   - Clear Filters fully clears tagSource/urlFilterRequested
========================================================= */

window.POC = window.POC || {};
var POC = window.POC;

POC.core = {
  ensureState() {
    POC.state = POC.state || {};

    POC.state.categories = Array.isArray(POC.state.categories) ? POC.state.categories : [];
    POC.state.productsByCategory = POC.state.productsByCategory || {};
    POC.state.productDetailCache = POC.state.productDetailCache || {};
    POC.state.selectedCategoryId = POC.state.selectedCategoryId || "";
    POC.state.selectedCategoryName = POC.state.selectedCategoryName || "Select a category";
    POC.state.initialized = !!POC.state.initialized;
    POC.state.hasLoadedProducts = !!POC.state.hasLoadedProducts;

    POC.state.filters = POC.state.filters || {};
    POC.state.filters.search = POC.state.filters.search || "";
    POC.state.filters.curatedOnly = !!POC.state.filters.curatedOnly;
    POC.state.filters.curatedFilter = POC.state.filters.curatedFilter || "ALL";
    POC.state.filters.brand = POC.state.filters.brand || "";
    POC.state.filters.color = POC.state.filters.color || "";
    POC.state.filters.size = POC.state.filters.size || "";
    POC.state.filters.tag = POC.state.filters.tag || "";
    POC.state.filters.tagSource = POC.state.filters.tagSource || "";
    POC.state.filters.urlFilterRequested = !!POC.state.filters.urlFilterRequested;

    POC.state.compare = POC.state.compare || {};
    POC.state.compare.selected = Array.isArray(POC.state.compare.selected)
      ? POC.state.compare.selected
      : [];
    POC.state.compare.max = Number(POC.state.compare.max) > 0
      ? Number(POC.state.compare.max)
      : 3;

    POC.state.modalPreview = POC.state.modalPreview || {
      productId: "",
      selectedStyleId: "",
      selectedViewIndex: 0
    };

    POC.state.categoryTree = POC.state.categoryTree || {
      expanded: {}
    };

    POC.state.paging = POC.state.paging || {
      skip: 0,
      take: 48,
      total: 0,
      hasMore: true,
      loadingMore: false
    };

    POC.state.pagination = POC.state.pagination || {};
    POC.state.pagination.batchSize = Number(POC.state.pagination.batchSize) > 0
      ? Number(POC.state.pagination.batchSize)
      : 48;

    POC.state.pagination.visibleCount = Number(POC.state.pagination.visibleCount) > 0
      ? Number(POC.state.pagination.visibleCount)
      : POC.state.pagination.batchSize;

    POC.state.sidebar = POC.state.sidebar || {};
    POC.state.sidebar.loadingAll = !!POC.state.sidebar.loadingAll;

    POC.state.sidebarAllCount = Number(POC.state.sidebarAllCount) > 0
      ? Number(POC.state.sidebarAllCount)
      : 0;
  },

  asString(value) {
    return value == null ? "" : String(value).trim();
  },

  escapeHtml(value) {
    return this.asString(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  },

  getProductIdValue(product) {
    if (!product || typeof product !== "object") return "";

    return this.asString(
      product.id ||
        product.ID ||
        product.Id ||
        product.productId ||
        product.ProductId ||
        product.ProductID ||
        product.inksoftProductId ||
        product.InkSoftProductId ||
        product.inksoft_product_id ||
        ""
    );
  },

  getCachedProductDetailForItem(product) {
    var productId = this.getProductIdValue(product);
    if (!productId || !POC.state || !POC.state.productDetailCache) return null;

    return (
      POC.state.productDetailCache[productId] ||
      POC.state.productDetailCache[String(productId)] ||
      null
    );
  },

  mergeProductWithCachedDetail(product) {
    var detail = this.getCachedProductDetailForItem(product);

    if (!detail || typeof detail !== "object") {
      return product || {};
    }

    /*
      Keep list/card fields, but allow detail fields to add Store Search Tags,
      web tags, style/color/size metadata, and alternate casing returned by InkSoft.
    */
    return Object.assign({}, product || {}, detail || {});
  },

  normalizeTagText(value) {
    return this.asString(value)
      .toLowerCase()
      .replace(/&amp;/g, "and")
      .replace(/[_\-]+/g, " ")
      .replace(/[^a-z0-9]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  },

  getFilterDisplayLabel(value) {
    var text = this.asString(value)
      .replace(/[_\-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    if (/^package spiritwear$/i.test(text)) {
      return "Spirit Wear Packages";
    }

    return text || "this filter";
  },

  splitTagValues(value) {
    if (Array.isArray(value)) {
      return value
        .reduce((items, item) => items.concat(this.splitTagValues(item)), [])
        .map((item) => this.asString(item))
        .filter(Boolean);
    }

    if (value && typeof value === "object") {
      var output = [];

      Object.keys(value).forEach((key) => {
        output = output.concat(this.splitTagValues(key));
        output = output.concat(this.splitTagValues(value[key]));
      });

      return output
        .map((item) => this.asString(item))
        .filter(Boolean);
    }

    return this.asString(value)
      .split(/[;,|]/g)
      .map((item) => item.trim())
      .filter(Boolean);
  },

  readObjectValueByLooseKey(source, wantedKeys) {
    if (!source || typeof source !== "object") return [];

    var wanted = (wantedKeys || []).map((key) => this.normalizeTagText(key));
    var output = [];

    Object.keys(source).forEach((key) => {
      var normalizedKey = this.normalizeTagText(key);

      if (wanted.indexOf(normalizedKey) >= 0) {
        output = output.concat(this.splitTagValues(source[key]));
      }
    });

    return output;
  },

  getProductTagValues(product) {
    if (!product || typeof product !== "object") return [];

    var mergedProduct = this.mergeProductWithCachedDetail(product);
    var values = [];

    [
      "tags",
      "Tags",
      "tag",
      "Tag",
      "storeSearchTags",
      "StoreSearchTags",
      "store_search_tags",
      "Store Search Tags",
      "searchTags",
      "SearchTags",
      "search_tags",
      "Search Tags",
      "productTags",
      "ProductTags",
      "product_tags",
      "webStoreTags",
      "WebStoreTags",
      "web_store_tags",
      "keywords",
      "Keywords",
      "searchKeywords",
      "SearchKeywords",
      "search_keywords",
      "labels",
      "Labels"
    ].forEach((key) => {
      values = values.concat(this.splitTagValues(mergedProduct[key]));
    });

    /*
      Some InkSoft/detail payloads put fields inside nested objects or arrays.
      Read loose key names so "Store Search Tags" and "storeSearchTags" both work.
    */
    [
      mergedProduct.customFields,
      mergedProduct.CustomFields,
      mergedProduct.metadata,
      mergedProduct.Metadata,
      mergedProduct.attributes,
      mergedProduct.Attributes,
      mergedProduct.product,
      mergedProduct.Product
    ].forEach((bucket) => {
      values = values.concat(
        this.readObjectValueByLooseKey(bucket, [
          "tags",
          "storeSearchTags",
          "Store Search Tags",
          "searchTags",
          "Search Tags",
          "keywords",
          "labels"
        ])
      );
    });

    if (Array.isArray(mergedProduct.storeSearchTags)) {
      values = values.concat(this.splitTagValues(mergedProduct.storeSearchTags));
    }

    var seen = {};

    return values.filter((item) => {
      var normalized = this.normalizeTagText(item);
      if (!normalized || seen[normalized]) return false;
      seen[normalized] = true;
      return true;
    });
  },

  productMatchesTag(product, requestedTag) {
    var wanted = this.normalizeTagText(requestedTag);
    if (!wanted) return true;

    var wantedCompact = wanted.replace(/\s+/g, "");
    var wantedUnderscore = wanted.replace(/\s+/g, "_");
    var tags = this.getProductTagValues(product);

    return tags.some((tag) => {
      var actual = this.normalizeTagText(tag);
      var actualCompact = actual.replace(/\s+/g, "");
      var actualUnderscore = actual.replace(/\s+/g, "_");

      return (
        actual === wanted ||
        actualCompact === wantedCompact ||
        actualUnderscore === wantedUnderscore ||
        actual.indexOf(wanted) !== -1 ||
        wanted.indexOf(actual) !== -1
      );
    });
  },

  getProductItemsFromPayload(payload) {
    if (!payload) return [];

    if (Array.isArray(payload.products)) return payload.products;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.results)) return payload.results;

    return [];
  },

  buildPayloadWithItems(payload, items) {
    var output = Object.assign({}, payload || {});
    var safeItems = Array.isArray(items) ? items : [];

    output.products = safeItems;
    output.data = safeItems;
    output.items = safeItems;
    output.results = safeItems;
    output.total = safeItems.length;
    output.count = safeItems.length;

    /*
      Important:
      Once a tag filter is active, the visible filtered result set is already local.
      Disable "has more" so another unfiltered page is not appended.
    */
    output.hasMore = false;

    return output;
  },

  applyActiveTagFilterToPayload(payload) {
    this.ensureState();

    var tag = this.ensureActiveTagFromUrl();
    if (!tag) return payload;

    var items = this.getProductItemsFromPayload(payload);
    var filtered = items.filter((product) => this.productMatchesTag(product, tag));

    var output = this.buildPayloadWithItems(payload, filtered);

    output.__pocActiveTag = tag;
    output.__pocActiveTagLabel = this.getFilterDisplayLabel(tag);
    output.__pocUrlFilterRequested = !!(POC.state.filters && POC.state.filters.urlFilterRequested);
    output.__pocNoTagMatches = output.__pocUrlFilterRequested && filtered.length === 0;

    window.__POC_LAST_TAG_FILTER__ = {
      tag: tag,
      tagSource: (POC.state.filters && POC.state.filters.tagSource) || "",
      normalizedTag: this.normalizeTagText(tag),
      displayLabel: this.getFilterDisplayLabel(tag),
      beforeCount: items.length,
      afterCount: filtered.length,
      noMatches: filtered.length === 0,
      matchedProducts: filtered.map((product) => ({
        id: this.getProductIdValue(product),
        name: product && (product.name || product.Name || product.title || product.Title || ""),
        tags: this.getProductTagValues(product)
      })),
      time: new Date().toISOString()
    };

    console.log("[POC] Product tag filter applied", window.__POC_LAST_TAG_FILTER__);

    return output;
  },

  readInitialFiltersFromUrl() {
    this.ensureState();

    try {
      var params = new URLSearchParams(window.location.search || "");

      /*
        Product package/tag links may arrive as:
          ?tag=Package_Spiritwear
          ?package=Package_Spiritwear

        Important: ?category= is reserved for product-category navigation
        and must not be interpreted as a Store Search Tag.
      */
      var candidates = [
        { key: "tag", value: params.get("tag") },
        { key: "productTag", value: params.get("productTag") },
        { key: "storeTag", value: params.get("storeTag") },
        { key: "searchTag", value: params.get("searchTag") },
        { key: "package", value: params.get("package") },
        { key: "packageTag", value: params.get("packageTag") },
        { key: "collection", value: params.get("collection") }
      ];

      var selected = candidates.find((item) => this.asString(item.value));

      if (selected) {
        POC.state.filters.tag = this.asString(selected.value);
        POC.state.filters.tagSource = selected.key;
        POC.state.filters.urlFilterRequested = true;
      }

      return {
        tag: POC.state.filters.tag || "",
        tagSource: POC.state.filters.tagSource || "",
        urlFilterRequested: !!POC.state.filters.urlFilterRequested
      };
    } catch (error) {
      console.warn("[POC] Unable to read URL filters.", error);

      return {
        tag: "",
        tagSource: "",
        urlFilterRequested: false
      };
    }
  },

  syncTagParamToUrl() {
    try {
      var tag = this.asString(POC.state.filters && POC.state.filters.tag);
      var source = this.asString(POC.state.filters && POC.state.filters.tagSource);
      var url = new URL(window.location.href);

      if (tag) {
        /*
          Preserve the original tag/package parameter when possible.
          The ?category= parameter is reserved for catalog navigation.
          Manual tag filters use ?tag=...
        */
        if (source && source !== "tag" && url.searchParams.has(source)) {
          url.searchParams.set(source, tag);
          url.searchParams.delete("tag");
        } else {
          url.searchParams.set("tag", tag);
        }
      } else {
        [
          "tag",
          "productTag",
          "storeTag",
          "searchTag",
          "package",
          "packageTag",
          "collection"
        ].forEach(function (key) {
          url.searchParams.delete(key);
        });
      }

      window.history.replaceState({}, "", url.toString());
    } catch (error) {
      console.warn("[POC] Unable to sync tag param to URL.", error);
    }
  },

  getActiveUrlTag() {
    try {
      var params = new URLSearchParams(window.location.search || "");

      var candidates = [
        { key: "tag", value: params.get("tag") },
        { key: "productTag", value: params.get("productTag") },
        { key: "storeTag", value: params.get("storeTag") },
        { key: "searchTag", value: params.get("searchTag") },
        { key: "package", value: params.get("package") },
        { key: "packageTag", value: params.get("packageTag") },
        { key: "collection", value: params.get("collection") }
      ];

      var selected = candidates.find((item) => this.asString(item.value));

      if (!selected) {
        return {
          tag: "",
          source: ""
        };
      }

      return {
        tag: this.asString(selected.value),
        source: selected.key
      };
    } catch (error) {
      return {
        tag: "",
        source: ""
      };
    }
  },

  ensureActiveTagFromUrl() {
    this.ensureState();

    var urlTag = this.getActiveUrlTag();

    if (urlTag && urlTag.tag) {
      POC.state.filters.tag = urlTag.tag;
      POC.state.filters.tagSource = urlTag.source || "";
      POC.state.filters.urlFilterRequested = true;
    }

    return POC.state.filters.tag || "";
  },

  getPayloadItemsReference(payload) {
    if (!payload) return [];

    if (Array.isArray(payload.products)) return payload.products;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.results)) return payload.results;

    return [];
  },

  setPayloadItemsReference(payload, items) {
    if (!payload) return payload;

    var safeItems = Array.isArray(items) ? items : [];

    payload.products = safeItems;
    payload.data = safeItems;
    payload.items = safeItems;
    payload.results = safeItems;
    payload.total = Math.max(Number(payload.total || payload.count || 0), safeItems.length);
    payload.count = Math.max(Number(payload.count || payload.total || 0), safeItems.length);

    return payload;
  },

  async ensureAllProductsLoadedForTagFilter(cacheKey, payload) {
    this.ensureState();

    var tag = this.ensureActiveTagFromUrl();

    if (!tag || !payload || !POC.api || typeof POC.api.getProductsByCategory !== "function") {
      return payload;
    }

    var key = String(cacheKey || POC.state.selectedCategoryId || "ALL");
    var take = Number(POC.state.paging && POC.state.paging.take) > 0
      ? Number(POC.state.paging.take)
      : 48;

    var items = this.getPayloadItemsReference(payload).slice();
    var seen = {};

    items.forEach((item) => {
      var id = this.getProductIdValue(item);
      if (id) seen[id] = true;
    });

    var total = Number(payload.total || payload.count || 0);
    var hasMore = payload.hasMore === true || (total > 0 && items.length < total);
    var skip = items.length;
    var safety = 0;

    while (hasMore && safety < 20) {
      safety += 1;

      var nextPayload = await POC.api.getProductsByCategory(key, {
        skip: skip,
        take: take
      });

      var nextItems = this.getProductItemsFromPayload(nextPayload);
      if (!nextItems.length) break;

      nextItems.forEach((item) => {
        var id = this.getProductIdValue(item) || ("row-" + items.length);
        if (seen[id]) return;

        seen[id] = true;
        items.push(item);
      });

      total = Math.max(
        total,
        Number(nextPayload && (nextPayload.total || nextPayload.count || 0)) || 0
      );

      skip = items.length;
      hasMore = nextPayload && nextPayload.hasMore === true;

      if (!hasMore && total > 0 && items.length < total) {
        hasMore = true;
      }
    }

    this.setPayloadItemsReference(payload, items);
    payload.hasMore = false;

    POC.state.productsByCategory[key] = payload;

    if (String(POC.state.selectedCategoryId || "") === key) {
      POC.state.paging.total = items.length;
      POC.state.paging.hasMore = false;
      POC.state.paging.skip = 0;
    }

    window.__POC_LAST_TAG_FULL_LOAD__ = {
      tag: tag,
      category: key,
      loadedCount: items.length,
      expectedTotal: total,
      safetyLoops: safety,
      time: new Date().toISOString()
    };

    console.log("[POC] Full product load for URL tag filter", window.__POC_LAST_TAG_FULL_LOAD__);

    return payload;
  },

  debugTagFilter() {
    this.ensureState();

    var payload = POC.state.productsByCategory[String(POC.state.selectedCategoryId || "ALL")];
    var items = this.getProductItemsFromPayload(payload);

    var rows = items.map((product) => ({
      id: product && (
        product.id ||
        product.ID ||
        product.Id ||
        product.productId ||
        product.ProductId ||
        ""
      ),
      name: product && (
        product.name ||
        product.Name ||
        product.title ||
        product.Title ||
        ""
      ),
      matchesActiveTag: this.productMatchesTag(product, POC.state.filters.tag),
      tags: this.getProductTagValues(product).join(", ")
    }));

    console.table(rows);

    return {
      activeTag: POC.state.filters.tag || "",
      normalizedTag: this.normalizeTagText(POC.state.filters.tag || ""),
      totalProductsInCurrentPayload: items.length,
      lastTagFilter: window.__POC_LAST_TAG_FILTER__ || null,
      rows: rows
    };
  },

  async hydrateProductsForTagFilter(payload) {
    this.ensureState();

    var tag = this.ensureActiveTagFromUrl();
    if (!tag) return payload;

    var items = this.getProductItemsFromPayload(payload);
    if (!items.length) return payload;

    /*
      The list payload often does not include InkSoft Store Search Tags.
      When a URL tag is active, hydrate product details first so tag filtering
      can use the full product record instead of the lightweight card record.
    */
    for (var i = 0; i < items.length; i += 1) {
      var productId = this.getProductIdValue(items[i]);
      if (!productId) continue;
      if (POC.state.productDetailCache && POC.state.productDetailCache[productId]) continue;

      try {
        await this.ensureProductDetailCached(productId);
      } catch (error) {
        console.warn("[POC] Unable to hydrate product detail for URL tag filter:", productId, error);
      }
    }

    return payload;
  },

  getBatchSize() {
    this.ensureState();

    return Number(POC.state.pagination.batchSize) > 0
      ? Number(POC.state.pagination.batchSize)
      : 48;
  },

  getVisibleCount() {
    this.ensureState();

    return Number(POC.state.pagination.visibleCount) > 0
      ? Number(POC.state.pagination.visibleCount)
      : this.getBatchSize();
  },

  resetVisibleCount() {
    this.ensureState();
    POC.state.pagination.visibleCount = this.getBatchSize();
  },

  increaseVisibleCount() {
    this.ensureState();
    POC.state.pagination.visibleCount = this.getVisibleCount() + this.getBatchSize();
  },

  setSidebarAllLoading(isLoading) {
    this.ensureState();

    POC.state.sidebar.loadingAll = !!isLoading;

    if (POC.render && typeof POC.render.setSidebarLoadingState === "function") {
      POC.render.setSidebarLoadingState(!!isLoading);
    }
  },

  rerenderCategories() {
    if (POC.render && typeof POC.render.renderCategories === "function") {
      POC.render.renderCategories(POC.state.categories || []);
    }
  },

  showInitialProductPrompt() {
    var headingEl = document.getElementById("poc-demo-products-heading");
    var countEl = document.getElementById("poc-demo-products-count");
    var gridEl = document.getElementById("poc-demo-products-grid");
    var statusEl = document.getElementById("poc-demo-products-status");
    var toolbarEl = document.getElementById("poc-demo-products-toolbar");
    var loadMoreWrap = document.getElementById("poc-demo-load-more-wrap");

    if (headingEl) headingEl.textContent = "Products";
    if (countEl) countEl.textContent = "";

    if (toolbarEl) {
      toolbarEl.style.display = "none";
    }

    if (loadMoreWrap) {
      loadMoreWrap.style.display = "none";
      loadMoreWrap.innerHTML = "";
    }

    if (statusEl) {
      statusEl.style.display = "";
      statusEl.className = "poc-demo-status poc-demo-status--info";
      statusEl.textContent = "Select a category on the left to load products.";
    }

    if (gridEl) {
      gridEl.innerHTML =
        '<div class="poc-demo-empty" style="grid-column:1/-1;">' +
          "Products are not loaded yet. Select a category to begin." +
        "</div>";
    }
  },

  getUrlFilterEmptyMessage(tag) {
    var label = this.getFilterDisplayLabel(tag);

    return {
      title: "No matching products found",
      body:
        "No products are currently available in " + label + ". " +
        "Try browsing all products or removing one or more filters.",
      status:
        "No products are currently available in " + label + "."
    };
  },

  renderUrlFilterEmptyState(tag) {
    var message = this.getUrlFilterEmptyMessage(tag);
    var gridEl = document.getElementById("poc-demo-products-grid");
    var countEl = document.getElementById("poc-demo-products-count");
    var statusEl = document.getElementById("poc-demo-products-status");
    var loadMoreWrap = document.getElementById("poc-demo-load-more-wrap");

    if (countEl) countEl.textContent = "0 item(s)";

    if (loadMoreWrap) {
      loadMoreWrap.style.display = "none";
      loadMoreWrap.innerHTML = "";
    }

    if (statusEl) {
      statusEl.style.display = "";
      statusEl.className = "poc-demo-status poc-demo-status--warning";
      statusEl.textContent = message.status;
    }

    if (gridEl) {
      gridEl.innerHTML =
        '<div class="poc-demo-empty poc-demo-empty--url-filter" style="grid-column:1/-1;">' +
          '<h3>' + this.escapeHtml(message.title) + '</h3>' +
          '<p>' + this.escapeHtml(message.body) + '</p>' +
          '<div class="poc-demo-empty__actions">' +
            '<a class="poc-demo-button" href="' + this.escapeHtml(window.location.pathname) + '">Browse All Products</a>' +
            '<button class="poc-demo-button poc-demo-button--secondary" type="button" data-clear-filters>Clear Filters</button>' +
          '</div>' +
        '</div>';
    }

    this.bindEmptyStateClearFilters();
  },

  bindEmptyStateClearFilters() {
    var self = this;

    Array.prototype.forEach.call(document.querySelectorAll("[data-clear-filters]"), function (button) {
      if (button.dataset.emptyStateBound) return;

      button.dataset.emptyStateBound = "true";

      button.addEventListener("click", function () {
        POC.state.filters.search = "";
        POC.state.filters.curatedOnly = false;
        POC.state.filters.curatedFilter = "ALL";
        POC.state.filters.brand = "";
        POC.state.filters.color = "";
        POC.state.filters.size = "";
        POC.state.filters.tag = "";
        POC.state.filters.tagSource = "";
        POC.state.filters.urlFilterRequested = false;

        try {
          var url = new URL(window.location.href);

          [
            "tag",
              "productTag",
            "storeTag",
            "searchTag",
            "package",
            "packageTag",
            "collection"
          ].forEach(function (key) {
            url.searchParams.delete(key);
          });

          window.history.replaceState({}, "", url.toString());
        } catch (error) {
          console.warn("[POC] Unable to clear URL filter params.", error);
        }

        self.resetVisibleCount();
        self.syncToolbarFromState();
        self.refreshCurrentGrid();
      });
    });
  },

  applyUrlFilterResultMessaging(renderPayload) {
    this.ensureState();

    var tag = (POC.state.filters && POC.state.filters.tag) || "";
    if (!tag) return;

    var lastTagFilter = window.__POC_LAST_TAG_FILTER__ || {};
    var afterCount = Number(lastTagFilter.afterCount || 0);
    var statusEl = document.getElementById("poc-demo-products-status");

    if (afterCount === 0 && POC.state.filters.urlFilterRequested) {
      this.renderUrlFilterEmptyState(tag);
      return;
    }

    if (statusEl) {
      statusEl.style.display = "";
      statusEl.className = "poc-demo-status poc-demo-status--info";
      statusEl.textContent =
        "Showing products included in " +
        this.getFilterDisplayLabel(tag) +
        ".";
    }
  },

  resetFiltersForCategoryNavigation() {
    this.ensureState();

    /*
      A sidebar category click is an explicit catalogue-navigation action.
      Clear any package/tag link and all toolbar filters before loading the
      requested category. Initial page loading does not call this method, so
      package/tag links still work when the page first opens.
    */
    POC.state.filters.search = "";
    POC.state.filters.curatedOnly = false;
    POC.state.filters.curatedFilter = "ALL";
    POC.state.filters.brand = "";
    POC.state.filters.color = "";
    POC.state.filters.size = "";
    POC.state.filters.tag = "";
    POC.state.filters.tagSource = "";
    POC.state.filters.urlFilterRequested = false;

    this.syncTagParamToUrl();
    this.resetVisibleCount();
    this.syncToolbarFromState();
  },

  bindToolbarEvents() {
    var self = this;

    var searchEl = document.getElementById("poc-demo-search");
    var curatedOnlyEl = document.getElementById("poc-demo-curated-only");
    var brandEl = document.getElementById("poc-demo-filter-brand");
    var colorEl = document.getElementById("poc-demo-filter-color");
    var sizeEl = document.getElementById("poc-demo-filter-size");
    var clearBtn = document.getElementById("poc-demo-clear-filters");
    var chips = document.querySelectorAll("[data-curated-filter]");

    if (searchEl && !searchEl.dataset.bound) {
      searchEl.dataset.bound = "true";

      searchEl.addEventListener("input", function () {
        POC.state.filters.search = searchEl.value || "";
        self.resetVisibleCount();
        self.refreshCurrentGrid();
      });
    }

    if (curatedOnlyEl && !curatedOnlyEl.dataset.bound) {
      curatedOnlyEl.dataset.bound = "true";

      curatedOnlyEl.addEventListener("change", function () {
        POC.state.filters.curatedOnly = !!curatedOnlyEl.checked;
        self.resetVisibleCount();
        self.refreshCurrentGrid();
      });
    }

    if (brandEl && !brandEl.dataset.bound) {
      brandEl.dataset.bound = "true";

      brandEl.addEventListener("change", function () {
        POC.state.filters.brand = brandEl.value || "";
        self.resetVisibleCount();
        self.refreshCurrentGrid();
      });
    }

    if (colorEl && !colorEl.dataset.bound) {
      colorEl.dataset.bound = "true";

      colorEl.addEventListener("change", function () {
        POC.state.filters.color = colorEl.value || "";
        self.resetVisibleCount();
        self.refreshCurrentGrid();
      });
    }

    if (sizeEl && !sizeEl.dataset.bound) {
      sizeEl.dataset.bound = "true";

      sizeEl.addEventListener("change", function () {
        POC.state.filters.size = sizeEl.value || "";
        self.resetVisibleCount();
        self.refreshCurrentGrid();
      });
    }

    if (clearBtn && !clearBtn.dataset.bound) {
      clearBtn.dataset.bound = "true";

      clearBtn.addEventListener("click", function () {
        POC.state.filters.search = "";
        POC.state.filters.curatedOnly = false;
        POC.state.filters.curatedFilter = "ALL";
        POC.state.filters.brand = "";
        POC.state.filters.color = "";
        POC.state.filters.size = "";
        POC.state.filters.tag = "";
        POC.state.filters.tagSource = "";
        POC.state.filters.urlFilterRequested = false;

        self.syncTagParamToUrl();
        self.resetVisibleCount();
        self.syncToolbarFromState();
        self.refreshCurrentGrid();
      });
    }

    Array.prototype.forEach.call(chips, function (chip) {
      if (chip.dataset.bound) return;

      chip.dataset.bound = "true";

      chip.addEventListener("click", function () {
        var value = chip.getAttribute("data-curated-filter") || "ALL";
        POC.state.filters.curatedFilter = value;

        Array.prototype.forEach.call(document.querySelectorAll("[data-curated-filter]"), function (button) {
          button.classList.toggle(
            "is-active",
            (button.getAttribute("data-curated-filter") || "ALL") === value
          );
        });

        self.resetVisibleCount();
        self.refreshCurrentGrid();
      });
    });
  },

  bindCompareEvents() {
    var self = this;

    Array.prototype.forEach.call(document.querySelectorAll("[data-compare-toggle]"), function (button) {
      if (button.dataset.bound) return;

      button.dataset.bound = "true";

      button.addEventListener("click", function () {
        var productId = button.getAttribute("data-compare-toggle") || "";
        if (!productId) return;
        self.toggleCompareProduct(productId);
      });
    });

    Array.prototype.forEach.call(document.querySelectorAll("[data-compare-remove]"), function (button) {
      if (button.dataset.bound) return;

      button.dataset.bound = "true";

      button.addEventListener("click", function () {
        var productId = button.getAttribute("data-compare-remove") || "";
        if (!productId) return;
        self.removeCompareProduct(productId);
      });
    });

    var clearCompareBtn = document.getElementById("poc-demo-compare-clear");

    if (clearCompareBtn && !clearCompareBtn.dataset.bound) {
      clearCompareBtn.dataset.bound = "true";

      clearCompareBtn.addEventListener("click", function () {
        self.clearCompareProducts();
      });
    }

    var openBtn = document.getElementById("poc-demo-compare-open");

    if (openBtn && !openBtn.dataset.bound) {
      openBtn.dataset.bound = "true";

      openBtn.addEventListener("click", function () {
        self.openCompareModal();
      });
    }
  },

  bindLoadMoreEvents() {
    var self = this;
    var button = document.getElementById("poc-demo-load-more");

    if (!button || button.dataset.bound) return;

    button.dataset.bound = "true";

    button.addEventListener("click", async function () {
      if (POC.state.paging.loadingMore) return;
      if (!POC.state.paging.hasMore) return;

      POC.state.paging.loadingMore = true;

      try {
        var nextSkip = POC.state.paging.skip + POC.state.paging.take;

        var nextPayload = await POC.api.getProductsByCategory(
          POC.state.selectedCategoryId,
          {
            skip: nextSkip,
            take: POC.state.paging.take
          }
        );

        var cacheKey = String(POC.state.selectedCategoryId);
        var existing = POC.state.productsByCategory[cacheKey];

        var existingItems =
          (existing && (existing.products || existing.data || existing.items)) || [];

        var newItems =
          nextPayload.products || nextPayload.data || nextPayload.items || [];

        var combined = existingItems.concat(newItems);

        existing.products = combined;
        existing.data = combined;
        existing.items = combined;

        POC.state.paging.skip = nextSkip;
        POC.state.paging.hasMore = nextPayload.hasMore === true;

        self.refreshCurrentGrid();
      } catch (err) {
        console.error("Load more failed:", err);
      }

      POC.state.paging.loadingMore = false;
    });
  },

  syncToolbarFromState() {
    var searchEl = document.getElementById("poc-demo-search");
    var curatedOnlyEl = document.getElementById("poc-demo-curated-only");
    var brandEl = document.getElementById("poc-demo-filter-brand");
    var colorEl = document.getElementById("poc-demo-filter-color");
    var sizeEl = document.getElementById("poc-demo-filter-size");
    var currentFilter = (POC.state.filters && POC.state.filters.curatedFilter) || "ALL";

    if (searchEl) searchEl.value = POC.state.filters.search || "";
    if (curatedOnlyEl) curatedOnlyEl.checked = !!POC.state.filters.curatedOnly;
    if (brandEl) brandEl.value = POC.state.filters.brand || "";
    if (colorEl) colorEl.value = POC.state.filters.color || "";
    if (sizeEl) sizeEl.value = POC.state.filters.size || "";

    Array.prototype.forEach.call(document.querySelectorAll("[data-curated-filter]"), function (button) {
      button.classList.toggle(
        "is-active",
        (button.getAttribute("data-curated-filter") || "ALL") === currentFilter
      );
    });
  },

  async refreshCurrentGrid() {
    this.ensureState();

    var cacheKey = String(POC.state.selectedCategoryId || "");

    if (!cacheKey || !POC.state.hasLoadedProducts) {
      this.showInitialProductPrompt();
      return;
    }

    var payload = POC.state.productsByCategory[cacheKey];
    if (!payload) return;

    payload = await this.ensureAllProductsLoadedForTagFilter(cacheKey, payload);
    await this.hydrateProductsForTagFilter(payload);

    var renderPayload = this.applyActiveTagFilterToPayload(payload);

    if (POC.render && typeof POC.render.renderProducts === "function") {
      POC.render.renderProducts(renderPayload);
    }

    this.applyUrlFilterResultMessaging(renderPayload);

    if (POC.render && POC.render.renderCompareTray) {
      POC.render.renderCompareTray();
    }

    this.bindCompareEvents();
    this.bindLoadMoreEvents();
    this.syncToolbarFromState();
    this.hydrateVisibleProductDetails(renderPayload);
  },

  getCurrentCategoryProducts() {
    var cacheKey = String(POC.state.selectedCategoryId || "");
    var payload = POC.state.productsByCategory[cacheKey];

    if (!payload) return [];

    return this.getProductItemsFromPayload(payload);
  },

  findProductInLoadedState(productId) {
    var target = String(productId || "");
    if (!target) return null;

    var categoryKeys = Object.keys(POC.state.productsByCategory || {});

    for (var i = 0; i < categoryKeys.length; i += 1) {
      var payload = POC.state.productsByCategory[categoryKeys[i]];
      var items = this.getProductItemsFromPayload(payload);

      for (var j = 0; j < items.length; j += 1) {
        var item = items[j];
        var id = item && item.id != null ? String(item.id) : "";
        if (id === target) return item;
      }
    }

    return null;
  },

  isCompareSelected(productId) {
    var target = String(productId || "");
    return (POC.state.compare.selected || []).indexOf(target) >= 0;
  },

  toggleCompareProduct(productId) {
    this.ensureState();

    var target = String(productId || "");
    if (!target) return;

    var selected = Array.isArray(POC.state.compare.selected)
      ? POC.state.compare.selected.slice()
      : [];

    var existingIndex = selected.indexOf(target);

    if (existingIndex >= 0) {
      selected.splice(existingIndex, 1);
      POC.state.compare.selected = selected;
      this.refreshCurrentGrid();
      return;
    }

    if (selected.length >= (POC.state.compare.max || 3)) {
      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus("You can compare up to 3 products only.", "error");
      }
      return;
    }

    selected.push(target);
    POC.state.compare.selected = selected;
    this.refreshCurrentGrid();
  },

  removeCompareProduct(productId) {
    this.ensureState();

    var target = String(productId || "");
    if (!target) return;

    POC.state.compare.selected = (POC.state.compare.selected || []).filter(function (id) {
      return String(id) !== target;
    });

    this.refreshCurrentGrid();
  },

  clearCompareProducts() {
    this.ensureState();
    POC.state.compare.selected = [];
    this.refreshCurrentGrid();
  },

  async ensureProductDetailCached(productId) {
    var cacheKey = String(productId || "");
    if (!cacheKey) return null;

    var product = POC.state.productDetailCache[cacheKey];
    if (product) return product;

    product = await POC.api.getProductDetail(cacheKey);
    POC.state.productDetailCache[cacheKey] = product;

    return product;
  },

  getRenderedProductIds(limit) {
    var safeLimit = Number(limit || this.getVisibleCount());

    return Array.prototype.map.call(
      document.querySelectorAll(".poc-demo-card[data-product-id]"),
      function (card) {
        return String(card.getAttribute("data-product-id") || "");
      }
    ).filter(Boolean).slice(0, safeLimit);
  },

  async hydrateVisibleProductDetails(payload) {
    var self = this;
    var renderedIds = this.getRenderedProductIds(this.getVisibleCount());

    if (!renderedIds.length) return;

    var hadNewDetail = false;

    for (var i = 0; i < renderedIds.length; i += 1) {
      var productId = renderedIds[i];

      if (!productId) continue;
      if (POC.state.productDetailCache[productId]) continue;

      try {
        await self.ensureProductDetailCached(productId);
        hadNewDetail = true;
      } catch (error) {
        console.warn("Failed to hydrate product detail for filters/swatches:", productId, error);
      }
    }

    if (hadNewDetail) {
      var renderPayload = self.applyActiveTagFilterToPayload(payload);

      if (POC.render && typeof POC.render.renderProducts === "function") {
        POC.render.renderProducts(renderPayload);
      }

      self.applyUrlFilterResultMessaging(renderPayload);

      if (POC.render && POC.render.renderCompareTray) {
        POC.render.renderCompareTray();
      }

      self.bindCompareEvents();
      self.bindLoadMoreEvents();
      self.syncToolbarFromState();
    }
  },

  async openCompareModal() {
    this.ensureState();

    var selectedIds = Array.isArray(POC.state.compare.selected)
      ? POC.state.compare.selected.slice(0, POC.state.compare.max || 3)
      : [];

    if (!selectedIds.length) return;

    try {
      POC.ui.openModal();

      var contentEl = document.getElementById("poc-demo-modal-content");

      if (contentEl) {
        contentEl.innerHTML = '<div class="poc-demo-modal__loading">Loading compare view…</div>';
      }

      var products = [];

      for (var i = 0; i < selectedIds.length; i += 1) {
        var detail = await this.ensureProductDetailCached(selectedIds[i]);
        if (detail) products.push(detail);
      }

      if (POC.render.renderCompareModal) {
        POC.render.renderCompareModal(products);
      } else if (contentEl) {
        contentEl.innerHTML = '<div class="poc-demo-modal__loading">Compare modal renderer is not available yet.</div>';
      }
    } catch (error) {
      console.error(error);

      var modalContentEl = document.getElementById("poc-demo-modal-content");

      if (modalContentEl) {
        modalContentEl.innerHTML = '<div class="poc-demo-modal__loading">Failed to load compare view.</div>';
      }
    }
  },

  async openProductPreview(productId) {
    this.ensureState();

    var id = String(productId || "");
    if (!id) return;

    try {
      POC.state.modalPreview = {
        productId: id,
        selectedStyleId: "",
        selectedViewIndex: 0
      };

      if (POC.ui && POC.ui.openModal) {
        POC.ui.openModal();
      }

      if (POC.render && POC.render.renderPreviewLoading) {
        POC.render.renderPreviewLoading();
      }

      var detail = await this.ensureProductDetailCached(id);

      if (!detail) {
        var el = document.getElementById("poc-demo-modal-content");

        if (el) {
          el.innerHTML = '<div class="poc-demo-modal__loading">Failed to load product preview.</div>';
        }

        return;
      }

      if (POC.render && POC.render.renderModalProduct) {
        POC.render.renderModalProduct(detail);
      }
    } catch (error) {
      console.error("Preview failed:", error);
    }
  },

  selectPreviewStyle(styleId) {
    POC.state.modalPreview.selectedStyleId = String(styleId || "");
    POC.state.modalPreview.selectedViewIndex = 0;

    var productId = POC.state.modalPreview.productId;
    var product = POC.state.productDetailCache[productId];

    if (product && POC.render && POC.render.renderModalProduct) {
      POC.render.renderModalProduct(product);
    }
  },

  selectPreviewView(viewIndex) {
    POC.state.modalPreview.selectedViewIndex = Number(viewIndex || 0);

    var productId = POC.state.modalPreview.productId;
    var product = POC.state.productDetailCache[productId];

    if (product && POC.render && POC.render.renderModalProduct) {
      POC.render.renderModalProduct(product);
    }
  },

  normalizeCategoriesPayload(payload) {
    var candidates = [
      payload,
      payload && payload.categories,
      payload && payload.Categories,
      payload && payload.data,
      payload && payload.Data,
      payload && payload.items,
      payload && payload.Items,
      payload && payload.results,
      payload && payload.Results,
      payload && payload.value,
      payload && payload.Value,
      payload && payload.data && payload.data.categories,
      payload && payload.data && payload.data.Categories,
      payload && payload.data && payload.data.items,
      payload && payload.data && payload.data.Items
    ];

    for (var i = 0; i < candidates.length; i += 1) {
      if (Array.isArray(candidates[i])) {
        return candidates[i];
      }
    }

    console.warn("[POC] Categories response did not contain an array:", payload);

    return [];
  },

  resolveInitialCategoryFromUrl() {
    this.ensureState();

    if (!POC.render || typeof POC.render.getCategoryParamFromUrl !== "function") {
      return {
        id: "ALL",
        name: "All Products"
      };
    }

    var slug = POC.render.getCategoryParamFromUrl();

    if (!slug) {
      return {
        id: "ALL",
        name: "All Products"
      };
    }

    var match = POC.render.findCategoryBySlug(POC.state.categories || [], slug);

    if (!match || match.id == null) {
      return {
        id: "ALL",
        name: "All Products"
      };
    }

    return {
      id: String(match.id),
      name: String(
        match.name ||
          match.Name ||
          match.CategoryName ||
          match.categoryName ||
          "Products"
      )
    };
  },

  async init() {
    this.ensureState();

    if (POC.state.initialized) return;

    try {
      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus("Loading categories…", "info");
      }

      this.readInitialFiltersFromUrl();

      this.setSidebarAllLoading(true);

      var rawCategories = await POC.api.getCategories();
      var categories = this.normalizeCategoriesPayload(rawCategories);

      POC.state.categories = categories;

      if (POC.render && typeof POC.render.renderCategories === "function") {
        POC.render.renderCategories(POC.state.categories);
      }

      var categoryTreeEl = document.getElementById("poc-demo-category-tree");

      if (categoryTreeEl) {
        categoryTreeEl.style.visibility = "visible";
        categoryTreeEl.style.display = "";
      }

      if (!categories.length) {
        console.warn("[POC] Category endpoint succeeded, but no category array was found.", rawCategories);
      }

      this.bindToolbarEvents();
      this.bindCompareEvents();

      if (POC.ui && POC.ui.bindModalClose) {
        POC.ui.bindModalClose();
      }

      var initialCategory = this.resolveInitialCategoryFromUrl();

      await this.loadCategory(
        initialCategory.id || "ALL",
        initialCategory.name || "All Products",
        false
      );

      POC.state.categoryTree = {
        expanded: {}
      };

      if (POC.render && typeof POC.render.renderCategories === "function") {
        POC.render.renderCategories(POC.state.categories || []);
      }

      var categoryTreeAfterLoad = document.getElementById("poc-demo-category-tree");

      if (categoryTreeAfterLoad) {
        categoryTreeAfterLoad.style.visibility = "visible";
        categoryTreeAfterLoad.style.display = "";
      }

      POC.state.initialized = true;
      this.setSidebarAllLoading(false);

      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus("", "info");
      }
    } catch (error) {
      this.setSidebarAllLoading(false);
      console.error("[POC] Initialization failed:", error);

      if (POC.render && typeof POC.render.renderCategories === "function") {
        POC.render.renderCategories(POC.state.categories || []);
      }

      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus(
          "Failed to initialize page. " + (error && error.message ? error.message : ""),
          "error"
        );
      }
    }
  },

  async loadCategory(categoryId, categoryName, showLoading) {
    this.ensureState();

    var cacheKey = String(categoryId || "ALL");
    var previousCategoryId = String(POC.state.selectedCategoryId || "");

    /*
      Do not clear POC.state.filters.tag here.
      The tag comes from the page URL, e.g.
      /pages/product?tag=Package_Spiritwear
      and must survive the initial All Products load.
    */
    if (previousCategoryId && previousCategoryId !== cacheKey) {
      POC.state.filters.search = "";
      POC.state.filters.curatedOnly = false;
      POC.state.filters.curatedFilter = "ALL";
      POC.state.filters.brand = "";
      POC.state.filters.color = "";
      POC.state.filters.size = "";
    }

    POC.state.selectedCategoryId = cacheKey;
    POC.state.selectedCategoryName = categoryName || "Products";
    POC.state.hasLoadedProducts = true;

    if (POC.render && POC.render.syncCategoryParamToUrl) {
      POC.render.syncCategoryParamToUrl(cacheKey, categoryName);
      this.syncTagParamToUrl();
    }

    if (showLoading !== false) {
      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus("Loading products…", "loading");
      }

      if (POC.ui && POC.ui.setProductsStatus) {
        POC.ui.setProductsStatus("Loading products…", "loading");

        var productsStatusEl = document.getElementById("poc-demo-products-status");

        if (productsStatusEl) {
          productsStatusEl.style.display = "";
        }
      }
    }

    this.resetVisibleCount();

    try {
      var payload = POC.state.productsByCategory[cacheKey];
      var shouldRefetch = cacheKey !== "ALL" || !payload;

      if (shouldRefetch) {
        delete POC.state.productsByCategory[cacheKey];

        POC.state.paging.skip = 0;

        payload = await POC.api.getProductsByCategory(cacheKey, {
          skip: 0,
          take: POC.state.paging.take
        });

        var payloadItems = [];

        if (payload) {
          if (Array.isArray(payload.data)) payloadItems = payload.data;
          else if (Array.isArray(payload.products)) payloadItems = payload.products;
          else if (Array.isArray(payload.items)) payloadItems = payload.items;
          else if (Array.isArray(payload.results)) payloadItems = payload.results;
        }

        if (payload && !Array.isArray(payload.data)) {
          payload.data = payloadItems;
        }

        if (payload && !Array.isArray(payload.products)) {
          payload.products = payloadItems;
        }

        if (payload && !Array.isArray(payload.items)) {
          payload.items = payloadItems;
        }

        POC.state.paging.total = Number(
          (payload && (payload.total || payload.count || payloadItems.length)) || 0
        );

        POC.state.paging.hasMore = !!(payload && payload.hasMore === true);

        POC.state.productsByCategory[cacheKey] = payload;
      }

      this.syncToolbarFromState();

      payload = await this.ensureAllProductsLoadedForTagFilter(cacheKey, payload);
      await this.hydrateProductsForTagFilter(payload);

      var renderPayload = this.applyActiveTagFilterToPayload(payload);

      if (POC.render && typeof POC.render.renderProducts === "function") {
        POC.render.renderProducts(renderPayload);
        this.applyUrlFilterResultMessaging(renderPayload);
      }

      this.setSidebarAllLoading(false);
      this.rerenderCategories();

      if (POC.render.renderCompareTray) {
        POC.render.renderCompareTray();
      }

      this.bindCompareEvents();
      this.bindLoadMoreEvents();
      this.syncToolbarFromState();

      await this.hydrateVisibleProductDetails(renderPayload);

      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus("", "");
      }

      var productsStatusElAfter = document.getElementById("poc-demo-products-status");

      if (productsStatusElAfter) {
        if (POC.state.filters && POC.state.filters.tag) {
          this.applyUrlFilterResultMessaging(renderPayload);
        } else {
          productsStatusElAfter.style.display = "none";
        }
      }
    } catch (error) {
      this.setSidebarAllLoading(false);
      this.rerenderCategories();

      console.error(error);

      if (POC.ui && POC.ui.setGlobalStatus) {
        POC.ui.setGlobalStatus(
          "Failed to load products. " + (error && error.message ? error.message : ""),
          "error"
        );
      }

      if (POC.ui && POC.ui.setProductsStatus) {
        POC.ui.setProductsStatus(
          "Failed to load products. " + (error && error.message ? error.message : ""),
          "error"
        );

        var errorProductsStatusEl = document.getElementById("poc-demo-products-status");

        if (errorProductsStatusEl) {
          errorProductsStatusEl.style.display = "";
        }
      }
    }
  },

  debugCurrentUrlTag() {
    this.readInitialFiltersFromUrl();
    return this.debugTagFilter();
  }
};


/* =========================================================
   Tag Render Guard
   Even if another part of the page calls POC.render.renderProducts with an
   unfiltered payload, force the current ?tag= / ?package= URL filter
   before rendering. The ?category= parameter is catalog navigation only.
========================================================= */

(function installPocTagRenderGuard() {
  window.POC = window.POC || {};
  var POC = window.POC;

  POC.__installTagRenderGuard = function () {
    if (!POC.render || typeof POC.render.renderProducts !== "function") return false;
    if (POC.render.renderProducts.__pocTagGuardInstalled) return true;

    var originalRenderProducts = POC.render.renderProducts;

    POC.render.renderProducts = function guardedRenderProducts(payload) {
      try {
        if (POC.core && typeof POC.core.applyActiveTagFilterToPayload === "function") {
          payload = POC.core.applyActiveTagFilterToPayload(payload);
        }
      } catch (error) {
        console.warn("[POC] Tag render guard failed; rendering original payload.", error);
      }

      return originalRenderProducts.call(this, payload);
    };

    POC.render.renderProducts.__pocTagGuardInstalled = true;
    return true;
  };

  POC.__installTagRenderGuard();

  document.addEventListener("DOMContentLoaded", function () {
    POC.__installTagRenderGuard();
  });

  window.setTimeout(function () {
    POC.__installTagRenderGuard();
  }, 250);
})();


/* =========================================================
   AUTO INIT
========================================================= */

document.addEventListener("DOMContentLoaded", function () {
  window.POC = window.POC || {};
  var POC = window.POC;

  if (POC.core && typeof POC.core.init === "function") {
    POC.core.init();
  }
});